import React, { Fragment } from 'react';

import { SecondaryButton } from '@components/Button/variants';
import ArticleFeedItem from '@components/FeedItems/ArticleFeedItem';
import BriefingFeedItem from '@components/FeedItems/BriefingFeedItem';
import Header from '@components/pages/Highlight/Header';
import useBookmarksList from '@hooks/useBookmarksList';
import useEventTracking from '@hooks/useEventTracking';
import { Article, Briefing } from '@interfaces/homepage';
import { ClickTarget, click } from '@tracking';
import { FeedContentType } from '@utils/enums';

interface ContentProps {
  bookmarks: (Article | Briefing)[];
  page: number;
  per_page: number;
  total_pages: number;
}

const Content = ({ bookmarks, page, per_page, total_pages }: ContentProps) => {
  const { trackEvent } = useEventTracking();
  const {
    bookmarks: bookmarkItems,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useBookmarksList({
    per_page,
    initialBookmarksContent: bookmarks,
    initialPage: page,
    initialTotalPages: total_pages,
  });

  const handleLoadMore = () => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    fetchNextPage({ cancelRefetch: false });
    trackEvent(click({ clickTarget: ClickTarget.ReadMore }));
  };

  return (
    <div>
      <Header header="Your Bookmarks" subheader="" />
      {bookmarkItems.length > 0 ? (
        <>
          {bookmarkItems.map((item, index) => (
            <Fragment key={`${item.contentType}-${item.id}`}>
              {item.contentType === FeedContentType.Article && (
                <ArticleFeedItem
                  authors={item.authors}
                  commentsCount={item.commentsCount}
                  contentIndex={index}
                  highlight={item.highlight}
                  id={item.id}
                  isBookmarked
                  isPro={item.isPro}
                  newsletterSlug={item.newsletterSlug}
                  picture={item.picture}
                  publishedAt={item.publishedAt}
                  skipPictureOptimization={item.skipPictureOptimization}
                  slug={item.slug}
                  title={item.title}
                />
              )}
              {item.contentType === FeedContentType.Briefing && (
                <BriefingFeedItem briefing={item} />
              )}
            </Fragment>
          ))}
          {hasNextPage && (
            <div className="px-16 pt-32 sm:px-0 sm:pt-48">
              <SecondaryButton
                className="bg-transparent focus:bg-transparent focus-visible:ring-0 max-sm:hover:!bg-transparent"
                as="button"
                fullWidth
                onClick={handleLoadMore}
                data-testid="read-more-button"
                isLoading={isFetchingNextPage}
              >
                Read more
              </SecondaryButton>
            </div>
          )}
        </>
      ) : (
        <div>No bookmarks found</div>
      )}
    </div>
  );
};

export default Content;
